import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Button from "../../../../../components/base/button/button";
import {Add, Delete, Edit} from "@mui/icons-material";
import BizPrintService from "../../../../../../core/services/biz-print/biz-print-service";
import {numComparator} from "../../../../../../core/services/utils/utils";
import BizPrintTemplatesDialog from "../../../../../components/dialogs/biz-print-templates";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import UpsertPrintPackingListDialog from "../../../../../components/dialogs/upsert-packing-list-dialog";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {Box, Paper, Tooltip, Typography} from "@mui/material";
import CustomerAutocomplete from "../../../../../components/text-fields/autocomplete/customer-autocomplete";
import ToastService from "../../../../../../core/services/toast";
import {PrintPackingListDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../../packages/data-grid";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import IconButton from "../../../../../components/base/button/icon_button";
import PartnerDataGrid from "../../../../../containers/data-grid";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const initialUpsertPackingListDialogState = {
    open: false,
}

const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}
const PrintPackingListView = () => {
    const [customer, setCustomer] = useState(null);
    const [packingLists, setPackingLists] = useState([]);
    const [upsertPackingListDialogState, setUpsertPackingListDialogState] = useState(initialUpsertPackingListDialogState);
    const [printing, setPrinting] = useState(false);
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const isMounted = useIsMounted();

    const {configureNotebookTags} = useConfigureNotebook('print-packing-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.printPackingList);
        configureBehavioralTool((tags) => tags.printPackingList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Opens the new packing list dialog
     * @type {(function(*))}
     */
    const openUpsertPackingListDialog = useCallback((data = null) => {
        setUpsertPackingListDialogState({
            open: true,
            data: data,
        })
    }, [])

    /**
     * Sets the customer in the state
     * @type {(function(*, *): void)}
     */
    const onCustomerChanged = useCallback((_, customer) => {
        setCustomer(customer);
    }, [])

    /**
     * Opens the biz print templates dialog
     * @type {(function(): Promise<void>)}
     */
    const openBizPrintDialog = useCallback(async () => {
        if (!customer)
            return;
        setPrinting(true);
        const customerResponse = await Api.getCustomerFullInformation(customer.id);
        if (!isMounted())
            return;
        setPrinting(false);
        const customerInfo = customerResponse?.data;
        if (!customerResponse?.resultFlag || !customerInfo) {
            ToastService.error({
                title: 'Error',
                message: 'Failed to get customer information for the selected customer.',
            });
            return;
        }
        setBizPrintDialogState({
            open: true,
            pageName: BizPrintService.pages.packingLists.name,
            pageData: {
                customer: {
                    name: customerInfo.name,
                    province: customerInfo.province,
                    country: customerInfo.countryTitle,
                    city: customerInfo.city,
                    address: customerInfo.address,
                    postalCode: customerInfo.postalCode,
                },
                packingLists: packingLists,
            }
        })

    }, [isMounted, packingLists, customer]);

    /**
     * Removes the packing list from the list of packing lists
     * @type {(function(*): void)}
     */
    const removePackingList = useCallback((packingList) => {
        setPackingLists(prevState => prevState.filter(e => e.id !== packingList.id));
    }, [])

    /**
     * Adds or Edits the packing list in the list of packing lists
     * @type {(function(*): void)}
     */
    const upsertPackingList = useCallback((packingList) => {
        setPackingLists(prevState => {
            const found = prevState.find(e => e.id === packingList.id);

            let adjustedList = prevState;
            // If packingList is not in prevState, add it
            if (!found) {
                adjustedList = [...prevState, packingList]
            }

            // Adjust the skidNumbers based on the change in the packingList's skidNumber
            adjustedList = adjustedList.map(e => {
                if (e.id === packingList.id) {
                    return packingList;
                }

                if (!found) {
                    if (e.skidNumber < packingList.skidNumber) {
                        return {...e, skidNumber: e.skidNumber - 1};
                    }
                    return {...e, skidNumber: e.skidNumber + 1};
                }

                // If skidNumber increased
                else if (found.skidNumber < packingList.skidNumber) {
                    if (e.skidNumber > found.skidNumber && e.skidNumber <= packingList.skidNumber) {
                        return {...e, skidNumber: e.skidNumber - 1};
                    }
                    if (e.skidNumber <= found.skidNumber) {
                        return {...e, skidNumber: e.skidNumber + 1};
                    }
                }

                // If skidNumber decreased
                else if (found.skidNumber > packingList.skidNumber) {
                    if (e.skidNumber >= packingList.skidNumber && e.skidNumber < found.skidNumber) {
                        return {...e, skidNumber: e.skidNumber + 1};
                    }
                    if (e.skidNumber < packingList.skidNumber) {
                        return {...e, skidNumber: e.skidNumber - 1};
                    }
                }

                return e;
            });

            // Sort and ensure continuous skidNumber values
            return adjustedList
                .sort((a, b) => numComparator(a.skidNumber, b.skidNumber))
                .map((e, index) => ({...e, skidNumber: index + 1}));
        });
    }, [])

    return (
        <>
            <div className={'print-packing-list-view'}>
                <div className={'my-4 d-flex align-items-center justify-content-between'}>
                    <Typography variant={'h6'} fontWeight={600}>
                        Print Packing Lists
                    </Typography>
                    <div className={'d-flex align-items-center justify-content-end'}>
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            disabled={printing}
                            onClick={() => openUpsertPackingListDialog(null)}
                            className={'d-flex align-items-center'}
                        >
                            <Add className='icon small'/>
                            Add new Packing List
                        </Button>
                        <Tooltip title={!customer ? "You must select a customer prior to printing your packing list" : ""}>
                            <Box component={'span'}>
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    disabled={!customer || !packingLists.length || printing}
                                    onClick={openBizPrintDialog}
                                    className={'d-flex align-items-center ml-2'}
                                >
                                    {
                                        printing
                                            ? "Printing..."
                                            : "Print"
                                    }
                                </Button>
                            </Box>
                        </Tooltip>
                    </div>
                </div>
                <Row>
                    <Col xs={12} className={'mb-4'}>
                        <CustomerAutocomplete
                            label={'Customer'}
                            fullWidth
                            value={customer}
                            onChange={onCustomerChanged}
                            disabled={printing}
                            textFieldProps={{
                                variant: 'filled',
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Col>
                    {
                        !packingLists.length
                            ? <Col xs={12} className={'mb-5 d-flex align-items-center justify-content-center'}>
                                <Paper sx={{padding: 3, width: '100%'}}>
                                    <Typography variant={'body2'} textAlign={'center'} marginBlock={10}>
                                        No Packing Lists added yet. please add a new packing list using the button above.
                                    </Typography>
                                </Paper>
                            </Col>
                            : packingLists.map((packingList) => (
                                <Col xs={12} lg={6} className={'mb-5 px-1'} key={packingList.id}>
                                    <PackingListCard
                                        packingList={packingList}
                                        total={packingLists.length}
                                        onRemove={removePackingList}
                                        onEdit={() => openUpsertPackingListDialog(packingList)}
                                    />
                                </Col>
                            ))
                    }
                </Row>
            </div>
            <BizPrintTemplatesDialog
                open={bizPrintDialogState.open}
                close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                pageName={bizPrintDialogState.pageName}
                pageData={bizPrintDialogState.pageData}
            />
            <UpsertPrintPackingListDialog
                open={upsertPackingListDialogState.open}
                close={() => setUpsertPackingListDialogState(initialUpsertPackingListDialogState)}
                packingList={upsertPackingListDialogState.data}
                submit={upsertPackingList}
                total={packingLists.length}
            />
        </>
    )
}


const PackingListCard = ({packingList, total, onRemove, onEdit}) => {

    const columns = useMemo(() =>
        /** @type {Array<DataGridColumn>}*/
        [
            {
                name: PrintPackingListDataGridColumnNames.index,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
                width: 0,
            },
            {
                name: PrintPackingListDataGridColumnNames.partNo,
                title: "Part No",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
            },
            {
                name: PrintPackingListDataGridColumnNames.serialNo,
                title: "Serial No",
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
            }
        ], [])


    /**@type {Array<DataGridRow>}*/
    const rows = useMemo(() => {
        return packingList.data.map((e, index) => ({
            key: index,
            data: e.data,
            cells: {
                [PrintPackingListDataGridColumnNames.index]: index + 1,
                [PrintPackingListDataGridColumnNames.partNo]:
                    <ProductInformationPopover partNo={e?.partNo}>
                        {e?.partNo ?? ""}
                    </ProductInformationPopover>,
                [PrintPackingListDataGridColumnNames.serialNo]: e.serialNo,
            }
        }))
    }, [packingList.data])


    return (
        <>
            <Paper className={'paper d-flex flex-column w-100 p-2'}>
                <div className={'d-flex d-flex align-items-center justify-content-end w-100'}>
                    <IconButton
                        className={"mx-1 ml-2"}
                        color={'secondary'}
                        size={'small'}
                        component={'span'}
                        onPressed={() => onEdit(packingList)}
                    >
                        <Edit className={'icon small'}/>
                    </IconButton>
                    <IconButton
                        className={"mx-1"}
                        color={'paper.error'}
                        size={'small'}
                        component={'span'}
                        onPressed={() => onRemove(packingList)}
                    >
                        <Delete className={'icon small'}/>
                    </IconButton>
                </div>
                <div className={'d-flex align-items-center justify-content-between w-100'}>
                    <p className={'text-sm font-weight-normal w-100 mb-2 mx-3'}>
                        SKID Number: <span
                        className={'font-weight-semi-bold secondary-color-1 tertiary-main'}>{packingList.skidNumber} of {total}</span>
                    </p>
                    <p className={'text-sm font-weight-normal w-100 mb-2 mx-3'}>
                        Ref#: <span
                        className={'font-weight-semi-bold secondary-color-1 tertiary-main'}>{packingList.refNo}</span>
                    </p>
                </div>
                <div className={'d-flex align-items-center justify-content-between w-100'}>
                    <p className={'text-sm font-weight-normal w-100 mb-2 mx-3'}>
                        SKID's Dimension: <span
                        className={'font-weight-semi-bold secondary-color-1 tertiary-main'}>{packingList.dimension}</span>
                    </p>
                    <p className={'text-sm font-weight-normal w-100 mb-2 mx-3'}>
                        Weight (LBS): <span
                        className={'font-weight-semi-bold secondary-color-1 tertiary-main'}>{packingList.weight}</span>
                    </p>
                </div>
                <PartnerDataGrid
                    rows={rows}
                    columns={columns}
                    visibleRows={10}
                    hideFooter
                    hideToolbar
                />
            </Paper>
        </>
    );
}


export default PrintPackingListView;
